// src/pages/BillingPage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { billingService } from '../services/api';
import './BillingPage.css';

// Check if user is an early adopter (first 100 users)
const isEarlyAdopter = (user) => {
  // You can implement logic based on user ID, registration date, or a specific flag
  return true; // TODO: Implement early adopter check
};

const PLAN_OPTIONS = [
  {
    key: "pro",
    name: "Pro",
    priceMonthly: "10€/mois",
    priceYearly: "100€/an",
    priceMonthlyAfter: "15€/mois",
    priceYearlyAfter: "150€/an",
    cta: "S'abonner au Pro",
    highlight: true,
    features: [
      { label: "Accès complet à la base d'articles", included: true },
      { label: "Accès aux brevets", included: true },
      { label: "Recherche avancée", included: true },
      { label: "Export CSV illimité", included: true },
      { label: "Support prioritaire", included: true }
    ]
  }
];

const STATUS_LABELS = {
  none: "Aucun abonnement",
  on_trial: "Essai gratuit",
  active: "Actif",
  past_due: "Paiement en retard",
  paused: "En pause",
  cancelled: "Annulé",
  expired: "Expiré"
};

const STATUS_COLORS = {
  none: "#6b7280",
  on_trial: "#3b82f6",
  active: "#10b981",
  past_due: "#f59e0b",
  paused: "#8b5cf6",
  cancelled: "#ef4444",
  expired: "#6b7280"
};

function BillingPage() {
  const { currentUser, updateUser } = useAuth();
  const [activePlan, setActivePlan] = useState(currentUser?.plan || "none");
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [billingCycles, setBillingCycles] = useState({
    pro: 'monthly'
  });
  const earlyAdopter = isEarlyAdopter(currentUser);

  useEffect(() => {
    if (currentUser?.plan && currentUser.plan !== activePlan) {
      setActivePlan(currentUser.plan);
    }
  }, [currentUser?.plan, activePlan]);

  useEffect(() => {
    let cancelled = false;
    const loadStatus = async () => {
      setStatusLoading(true);
      try {
        const response = await billingService.getStatus();
        if (!cancelled && response?.success && response.user) {
          updateUser(response.user);
          setActivePlan(response.user.plan || "free");
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Erreur lors de la récupération du statut:", err);
        }
      } finally {
        if (!cancelled) {
          setStatusLoading(false);
        }
      }
    };
    loadStatus();
    return () => {
      cancelled = true;
    };
  }, [updateUser]);

  const handleSelectPlan = async (planKey) => {
    setError(null);
    setMessage(null);

    if (planKey === activePlan && activePlan === 'pro') {
      setMessage("Ce plan est déjà actif.");
      return;
    }

    setLoadingPlan(planKey);
    try {
      const billingCycle = billingCycles[planKey] || 'monthly';
      const response = await billingService.createCheckout(planKey, billingCycle);
      if (!response?.success) {
        throw new Error(response?.error || "Impossible de créer la session de paiement");
      }

      if (response.url) {
        window.location.href = response.url;
      } else {
        setMessage("Session de paiement créée.");
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Erreur lors de la création du paiement";
      setError(msg);
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleBillingCycleChange = (planKey, cycle) => {
    setBillingCycles(prev => ({
      ...prev,
      [planKey]: cycle
    }));
  };

  const currentPlanInfo = PLAN_OPTIONS.find((plan) => plan.key === activePlan) || PLAN_OPTIONS[0];
  const subscriptionStatus = currentUser?.subscriptionStatus || 'none';
  const statusLabel = STATUS_LABELS[subscriptionStatus] || subscriptionStatus;
  const statusColor = STATUS_COLORS[subscriptionStatus] || "#6b7280";

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDisplayPrice = (plan) => {
    if (!plan.priceMonthly) return plan.price;

    const cycle = billingCycles[plan.key] || 'monthly';
    if (earlyAdopter) {
      return cycle === 'monthly' ? plan.priceMonthly : plan.priceYearly;
    } else {
      return cycle === 'monthly' ? plan.priceMonthlyAfter : plan.priceYearlyAfter;
    }
  };

  return (
    <div className="billing-page">
      <div className="billing-header">
        <h1>Abonnement et facturation</h1>
        <p>Gérez votre abonnement et consultez votre historique de paiement</p>
      </div>

      {earlyAdopter && (
        <div className="billing-early-adopter-banner">
          🎉 Vous bénéficiez du prix Early Adopter (100 premiers utilisateurs)
        </div>
      )}

      {/* Current Subscription Overview */}
      <div className="billing-current-plan">
        <div className="billing-current-plan-header">
          <h2>Abonnement actuel</h2>
          {statusLoading && <span className="billing-loading">Mise à jour...</span>}
        </div>

        <div className="billing-current-plan-content">
          <div className="billing-plan-info">
            <div className="billing-plan-name">
              <span className="billing-plan-badge">{currentPlanInfo.name}</span>
              <span className="billing-plan-price">{getDisplayPrice(currentPlanInfo)}</span>
            </div>
            <div className="billing-plan-status">
              <span
                className="billing-status-badge"
                style={{ backgroundColor: statusColor }}
              >
                {statusLabel}
              </span>
            </div>
          </div>

          {activePlan === 'pro' && currentUser?.subscriptionRenewsAt && (
            <div className="billing-plan-details">
              <div className="billing-detail-item">
                <span className="billing-detail-label">Prochain renouvellement</span>
                <span className="billing-detail-value">{formatDate(currentUser.subscriptionRenewsAt)}</span>
              </div>
            </div>
          )}

          {activePlan === 'pro' && currentUser?.subscriptionEndsAt && (
            <div className="billing-plan-details">
              <div className="billing-detail-item">
                <span className="billing-detail-label">Date de fin</span>
                <span className="billing-detail-value">{formatDate(currentUser.subscriptionEndsAt)}</span>
              </div>
            </div>
          )}

          {subscriptionStatus === 'on_trial' && currentUser?.trialEndAt && (
            <div className="billing-plan-details">
              <div className="billing-detail-item">
                <span className="billing-detail-label">Fin de l'essai gratuit</span>
                <span className="billing-detail-value">{formatDate(currentUser.trialEndAt)}</span>
              </div>
            </div>
          )}

          {activePlan === 'pro' && currentUser?.lemonSqueezyCustomerId && (
            <div className="billing-plan-actions">
              <a
                href="https://app.lemonsqueezy.com/my-orders"
                target="_blank"
                rel="noopener noreferrer"
                className="billing-btn-secondary"
              >
                Gérer sur Lemon Squeezy
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      {message && <div className="billing-message success">{message}</div>}
      {error && <div className="billing-message error">{error}</div>}

      {/* Available Plans */}
      <div className="billing-plans-section">
        <h2>Plans disponibles</h2>
        <p className="billing-plans-subtitle">Changez de plan à tout moment • Sans engagement</p>

        <div className="billing-plans-grid">
          {PLAN_OPTIONS.map((plan) => {
            const isCurrent = plan.key === activePlan;
            const isLoading = loadingPlan === plan.key;
            const disabled = isCurrent || isLoading;
            const currentCycle = billingCycles[plan.key] || 'monthly';

            return (
              <div
                key={plan.key}
                className={`billing-plan-card ${plan.highlight ? 'highlight' : ''} ${isCurrent ? 'current' : ''}`}
              >
                {plan.highlight && earlyAdopter && <div className="billing-plan-badge-popular">Early Adopter</div>}

                <div className="billing-plan-card-header">
                  <h3>{plan.name}</h3>

                  {plan.priceMonthly && plan.priceYearly && (
                    <div className="billing-cycle-toggle">
                      <button
                        className={currentCycle === 'monthly' ? 'active' : ''}
                        onClick={() => handleBillingCycleChange(plan.key, 'monthly')}
                      >
                        Mensuel
                      </button>
                      <button
                        className={currentCycle === 'yearly' ? 'active' : ''}
                        onClick={() => handleBillingCycleChange(plan.key, 'yearly')}
                      >
                        Annuel
                      </button>
                    </div>
                  )}

                  <div className="billing-plan-card-price">{getDisplayPrice(plan)}</div>

                  {plan.priceMonthly && !earlyAdopter && (
                    <div className="billing-price-after">
                      Prix normal: {currentCycle === 'monthly' ? plan.priceMonthlyAfter : plan.priceYearlyAfter}
                    </div>
                  )}

                  {currentCycle === 'yearly' && plan.priceYearly && (
                    <div className="billing-yearly-savings">
                      Économisez {earlyAdopter ? '20€' : '30€'} par an
                    </div>
                  )}
                </div>

                <ul className="billing-plan-features">
                  {plan.features.map((feature, index) => (
                    <li key={index} className={feature.included ? 'included' : 'excluded'}>
                      <span className="billing-feature-icon">
                        {feature.included ? '✓' : '✕'}
                      </span>
                      <span>{feature.label}</span>
                    </li>
                  ))}
                </ul>

                {subscriptionStatus === 'on_trial' && (
                  <div className="billing-trial-notice">
                    🎉 7 jours d'essai gratuit en cours
                  </div>
                )}

                <button
                  onClick={() => handleSelectPlan(plan.key)}
                  disabled={disabled}
                  className={`billing-plan-btn ${isCurrent ? 'current' : ''}`}
                >
                  {isLoading ? 'Chargement...' : isCurrent ? 'Plan actuel' : plan.cta}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Help Section */}
      <div className="billing-help-section">
        <h3>Besoin d'aide ?</h3>
        <p>Contactez notre support pour toute question concernant votre abonnement ou votre facturation.</p>
        <a href="mailto:support@datasaas.com" className="billing-btn-secondary">
          Contacter le support
        </a>
      </div>
    </div>
  );
}

export default BillingPage;
